import { Card, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, CardContent, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { writeToClipboard } from "@src/appV2/NativeAdapters/clipboard.adapter";
import { Button } from "@src/appV2/redesign/components/Button";

import { useGetProfessionalReference } from "../api/useGetProfessionalReference";
import { PROFESSIONAL_REFERENCE_STATUS, PROFESSIONAL_SHARE_FLOW } from "../constants";
import { type ProfessionalReference } from "../types";
import { ProfessionalReferencesStatusPill } from "./StatusPill";

export interface ReferenceCardProps {
  reference: ProfessionalReference;
  completed: boolean;
}

export function ReferenceCard({ reference, completed }: ReferenceCardProps) {
  const { showSuccessToast } = useToast();
  const isCopyLinkEnabled =
    reference.status === PROFESSIONAL_REFERENCE_STATUS.REQUESTED && !completed;

  const { data: professionalReferenceData, isLoading: isLoadingProfessionalReference } =
    useGetProfessionalReference(reference._id, {
      enabled: isCopyLinkEnabled,
    });

  const referenceFullName = [reference.referenceFirstName, reference.referenceLastName]
    .filter(Boolean)
    .join(" ");

  return (
    <Card
      outlined
      variant="tertiary"
      sx={{ p: 2 }}
      role="article"
      aria-label="Professional reference"
    >
      <CardContent>
        <Stack spacing={5}>
          {/* Reference status pill */}
          <ProfessionalReferencesStatusPill
            status={reference.status}
            referenceFirstName={reference.referenceFirstName}
          />

          {/* Reference details */}
          <Text semibold variant="h5">
            {referenceFullName}
          </Text>

          <Stack gap={1}>
            <Text variant="body2">Employer</Text>
            <Stack>
              <Text semibold variant="h5">
                {reference.employerName}
              </Text>
            </Stack>
          </Stack>

          {/* Copy link button for pending references */}
          {isCopyLinkEnabled && (
            <Box width="100%" sx={{ pt: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                disabled={isLoadingProfessionalReference || !isDefined(professionalReferenceData)}
                onClick={async () => {
                  if (!isDefined(professionalReferenceData)) {
                    return;
                  }

                  await writeToClipboard(professionalReferenceData.inviteMessage.webFormLink);
                  logEvent(APP_V2_USER_EVENTS.PROFESSIONAL_REFERENCE_SHARED, {
                    referenceId: reference._id,
                    flow: PROFESSIONAL_SHARE_FLOW.REMIND,
                  });
                  showSuccessToast("Link copied. Share with your reference via message or email");
                }}
              >
                Copy Reference Link
              </Button>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
